import type { useGT } from 'gt-react';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { BulkOverrideLineResult } from './bulkFulfillmentOverride';

type Gt = ReturnType<typeof useGT>;

type SkippedBulkOverrideLine = Extract<
  BulkOverrideLineResult,
  { status: 'skipped' }
>;

export function bulkOverrideSkipLabel(
  line: AssignmentLine | undefined,
): string {
  if (line == null) return '';
  return line.productSku ?? line.productV3Key ?? line.suggestionId;
}

export function bulkOverrideSkipReasonText(gt: Gt, reason: string): string {
  switch (reason) {
    case 'vendor_unavailable':
      return gt('vendor not available for this item', {
        context: 'bulk override skip reason vendor',
      });
    case 'source_unavailable':
      return gt('transfer source not available for this item', {
        context: 'bulk override skip reason source',
      });
    case 'trashed':
      return gt('line is trashed', {
        context: 'bulk override skip reason trashed',
      });
    case 'released':
      return gt('line is already released', {
        context: 'bulk override skip reason, line already on an order',
      });
    case 'no_product':
      return gt('no product on this line', {
        context: 'bulk override skip reason no product',
      });
    case 'substitute_quantity':
      return gt(
        'line is planned as a substitute; set a quantity to override it',
        {
          context: 'bulk override skip reason substitute line without quantity',
        },
      );
    case 'make_quantity':
      return gt(
        'line is planned as a make from components; set a quantity to override it',
        {
          context: 'bulk override skip reason make line without quantity',
        },
      );
    case 'contract_unavailable':
      return gt('contract price not available for this item', {
        context: 'bulk override skip reason contract unavailable',
      });
    case 'contract_not_applicable':
      return gt('contract price cannot apply to this line', {
        context: 'bulk override skip reason contract not applicable',
      });
    default:
      return reason;
  }
}

export function bulkOverrideHomogeneousSkipReason(
  skipped: readonly SkippedBulkOverrideLine[],
): string | null {
  if (skipped.length === 0) return null;
  const first = skipped[0].reason;
  return skipped.every((row) => row.reason === first) ? first : null;
}

export function bulkOverrideSkippedSkuList(
  skipped: readonly SkippedBulkOverrideLine[],
  lines: readonly AssignmentLine[],
): string {
  const labels = skipped.map((row) => {
    const line = lines.find((l) => l.suggestionId === row.suggestionId);
    return bulkOverrideSkipLabel(line);
  });
  return labels.filter((label) => label !== '').join(', ');
}

export function bulkOverrideMultiSkipToastMessage(
  gt: Gt,
  skippedCount: number,
  skus: string,
  reason: string,
): string {
  if (reason === 'contract_unavailable') {
    return gt(
      'Skipped {n} lines ({skus}): contract not available for that item.',
      {
        context: 'bulk contract price override multi skip toast',
        n: skippedCount,
        skus,
      },
    );
  }
  return gt('Skipped {n} lines ({skus}): {reason}', {
    context: 'bulk fulfillment override multi skip toast',
    n: skippedCount,
    skus,
    reason: bulkOverrideSkipReasonText(gt, reason),
  });
}
